import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Group, useBreakpoint } from '@landr/maestro';
import { PlayerWaveformDataPoints } from '@landr/react-player';
import { StreamEntityType } from 'types/enums';
import { DownloadButton } from 'components/CommonUi/DownloadButton';
import { Sample } from 'types';
import PlayerButton from 'components/CommonUi/PlayerButton/PlayerButton';
import PlayerWaveform from 'components/PlayerWaveform/PlayerWaveform';
import { useWaveformData } from 'hooks/useWaveformData';
import { MetasText } from 'components/MetasText/MetasText';
import { FavoriteButton } from 'components/FavoriteButton';
import { CreatorButton } from 'components/CommonUi/CreatorButton/CreatorButton';
import { analytics } from 'utils/analytics';
import {
    CtaGoal,
    CtaType,
    InteractionSource,
} from 'utils/analytics/AnalyticsV2Service.generated';
import {
    PlayerProvider,
    usePlayerContext,
} from 'contexts/PlayerContext/PlayerContext';
import { getMetas } from 'helpers/getMetas/getMetas';
import { usePreviewContext } from '../PreviewContext';
import { usePreviewKeyBoardContext } from '../hooks/keyboardsEvents/usePreviewKeyBoardProvider';
import * as Styled from './styled';

export const _PreviewSamplesRow = ({
    sample,
    dataPoints,
    showCreatorButton,
    handleDownloadClicked,
    handleFavoriteClicked,
    isFocused,
    rowIndex,
    rowRef,
}: {
    sample: Sample;
    dataPoints: PlayerWaveformDataPoints;
    showCreatorButton: boolean;
    handleDownloadClicked: () => void;
    handleFavoriteClicked: () => void;
    isFocused: boolean;
    rowIndex: number;
    rowRef: React.RefObject<HTMLDivElement>;
}) => {
    const { setIndexFocusedByClick } = usePreviewKeyBoardContext();
    const [isCurrentlyFocused, setIsCurrentFocused] = useState(false);

    const { playFromBeginning, seek } = usePlayerContext();

    // re-init waveform currentTime on mount
    useEffect(() => {
        seek && seek(0, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isFocused && rowRef.current !== null) {
            (document.activeElement as HTMLElement).blur();
            rowRef.current?.focus();
            playFromBeginning();
            setIsCurrentFocused(true);
        } else {
            setIsCurrentFocused(false);
        }
    }, [isFocused, rowRef, setIsCurrentFocused, playFromBeginning]);

    const updateCurrentPreviewIndex = useCallback(() => {
        setIndexFocusedByClick(rowIndex);
    }, [setIndexFocusedByClick, rowIndex]);

    const metas = getMetas(sample);

    return (
        <Styled.PreviewSamplesRowItem
            ref={rowRef}
            tabIndex={0}
            onClick={updateCurrentPreviewIndex}
            isCurrentlyFocused={isCurrentlyFocused}
        >
            <Styled.PreviewSamplesRowGroup>
                <Group.Item>
                    <PlayerButton
                        id={sample.guid}
                        stopPropagation={false}
                        isRounded
                        kind="minimal"
                        variant="secondary"
                        size="sm"
                        keyboardEventConsumerRef={rowRef}
                    />
                </Group.Item>
                <Styled.TagsAndWaveform>
                    <Styled.WaveformContainer>
                        <PlayerWaveform
                            guid={sample.guid}
                            dataPoints={dataPoints}
                        />
                    </Styled.WaveformContainer>
                    <MetasText
                        metas={metas}
                        showExtra={false}
                        contrast="base"
                    />
                </Styled.TagsAndWaveform>
                {showCreatorButton && (
                    <Group.Item>
                        <CreatorButton
                            sample={sample}
                            isRounded
                            kind="minimal"
                            variant="secondary"
                            size="sm"
                        />
                    </Group.Item>
                )}
                <Group.Item>
                    <FavoriteButton
                        key={`reconciliation-isFavorited-${sample.isFavorited}`}
                        item={sample}
                        isRounded
                        kind="minimal"
                        variant="secondary"
                        size="sm"
                        onClick={handleFavoriteClicked}
                    />
                </Group.Item>
                <Group.Item>
                    <DownloadButton
                        item={sample}
                        isRounded
                        kind="minimal"
                        variant="secondary"
                        size="sm"
                        onClick={handleDownloadClicked}
                    />
                </Group.Item>
            </Styled.PreviewSamplesRowGroup>
        </Styled.PreviewSamplesRowItem>
    );
};

const PreviewSamplesRow = ({
    sample,
    isFocused,
    rowIndex,
}: {
    sample: Sample;
    isFocused: boolean;
    rowIndex: number;
}) => {
    const rowRef = useRef<HTMLDivElement>(null);
    const showCreatorButton = !useBreakpoint('xl');
    const { dataPoints } = useWaveformData(sample);

    const { contentGroup, contentList } = usePreviewContext();

    const handleDownloadClicked = () => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.DownloadSample,
            ctaType: CtaType.Button,
            interactionSource: InteractionSource.AssetAction,
            ctaText: sample.name,
            ctaContentGroup: contentGroup,
            ctaContentList: contentList,
        });
    };

    const handleFavoriteClicked = () => {
        analytics.trackCTAClicked({
            ctaGoal: CtaGoal.FavouriteSample,
            ctaType: CtaType.Button,
            interactionSource: InteractionSource.AssetAction,
            ctaText: sample.name,
            ctaContentGroup: contentGroup,
            ctaContentList: contentList,
        });
    };

    return (
        <PlayerProvider
            playerId={sample.guid}
            streamEntityType={StreamEntityType.Samples}
        >
            <_PreviewSamplesRow
                rowRef={rowRef}
                sample={sample}
                dataPoints={dataPoints}
                showCreatorButton={showCreatorButton}
                isFocused={isFocused}
                rowIndex={rowIndex}
                handleDownloadClicked={handleDownloadClicked}
                handleFavoriteClicked={handleFavoriteClicked}
            />
        </PlayerProvider>
    );
};

export default PreviewSamplesRow;
